/**
 * AdjustPreferencesSheet — bottom sheet for adjusting retreat preferences.
 *
 * Collects wake/meal time preferences from the user and calls
 * onPreferencesChange immediately on every input change (live re-render
 * in the parent via useScheduleGenerator).
 *
 * UI-INV-005: Leaf component, props only, no engine imports.
 */
import type { UserRetreatPreferences } from "@retreats/content";
import { TEST_IDS } from "../testing/testIds";
import styles from "./AdjustPreferencesSheet.module.css";

type Props = {
  open: boolean;
  preferences: UserRetreatPreferences;
  tradition: "zen" | "vipassana";
  defaultWakeHour: number;
  isStartNowMode: boolean;
  onPreferencesChange: (prefs: UserRetreatPreferences) => void;
  onClose: () => void;
  onStartTomorrow?: () => void;
};

function hourToTimeStr(hour: number): string {
  const h = Math.floor(hour);
  const m = Math.round((hour % 1) * 60);
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

function formatDefaultWakeHour(hour: number): string {
  const h = Math.floor(hour);
  const period = h < 12 ? "AM" : "PM";
  const display = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${display}:00 ${period}`;
}

export function AdjustPreferencesSheet({
  open,
  preferences,
  tradition,
  defaultWakeHour,
  isStartNowMode,
  onPreferencesChange,
  onClose,
  onStartTomorrow,
}: Props) {
  if (!open) return null;

  const update = (patch: Partial<UserRetreatPreferences>) =>
    onPreferencesChange({ ...preferences, ...patch });

  const defaultWakeStr = hourToTimeStr(defaultWakeHour);

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" data-testid={TEST_IDS.ADJUST_SHEET}>
      <div className={styles.sheet}>
        {/* Handle */}
        <div className={styles.handle} />

        <div className={styles.header}>
          <span className={styles.title}>Adjust times</span>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            type="button"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className={styles.fields}>
          {/* Wake time — both traditions */}
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="adj-wake-time">
              Wake time
            </label>
            <input
              id="adj-wake-time"
              type="time"
              step="900"
              className={styles.timeInput}
              value={preferences.wakeTime ?? defaultWakeStr}
              onChange={(e) => update({ wakeTime: e.target.value })}
            />
          </div>

          {/* Meal times */}
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="adj-breakfast">
              Breakfast
            </label>
            <input
              id="adj-breakfast"
              type="time"
              step="900"
              className={styles.timeInput}
              value={preferences.breakfastTime ?? "07:00"}
              onChange={(e) => update({ breakfastTime: e.target.value })}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="adj-lunch">
              Lunch
            </label>
            <input
              id="adj-lunch"
              type="time"
              step="900"
              className={styles.timeInput}
              value={preferences.lunchTime ?? "12:00"}
              onChange={(e) => update({ lunchTime: e.target.value })}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="adj-dinner">
              Dinner
            </label>
            <input
              id="adj-dinner"
              type="time"
              step="900"
              className={styles.timeInput}
              value={preferences.dinnerTime ?? "18:00"}
              onChange={(e) => update({ dinnerTime: e.target.value })}
            />
          </div>

          {/* Tea time — vipassana has tea breaks */}
          {tradition === "vipassana" && (
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="adj-tea">
                Tea break
              </label>
              <input
                id="adj-tea"
                type="time"
                step="900"
                className={styles.timeInput}
                value={preferences.teaTime ?? "17:00"}
                onChange={(e) => update({ teaTime: e.target.value })}
              />
            </div>
          )}

          {/* Path A only: offer to start tomorrow instead */}
          {isStartNowMode && onStartTomorrow && (
            <button
              className={styles.tomorrowBtn}
              onClick={onStartTomorrow}
              type="button"
              data-testid={TEST_IDS.ADJUST_SHEET_START_TOMORROW}
            >
              Start tomorrow at {formatDefaultWakeHour(defaultWakeHour)} instead
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
